import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Plus, Pencil, Trash2, Power, Send, CalendarClock, Star, History, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";

// Session Z34 — Cleaning companies CRUD
// Session Z35 — Rota, email blast, rating aggregates
const EMPTY = {
  name: "", contact_name: "", email: "", phone: "",
  website: "", notes: "", active: true, whatsapp_enabled: false,
};

function StarBar({ value, count, size = "sm" }) {
  if (!count) {
    return <span className="text-[10px] text-slate-400 italic">no ratings yet</span>;
  }
  const rounded = Math.round(value || 0);
  return (
    <span className="inline-flex items-center gap-0.5" title={`${value}★ from ${count} rating${count === 1 ? "" : "s"}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`${size === "lg" ? "text-base" : "text-xs"} leading-none ${
            n <= rounded ? "text-amber-500" : "text-slate-200"
          }`}
        >
          ★
        </span>
      ))}
      <span className="ml-1 text-[10px] text-slate-500 font-medium">
        {value?.toFixed(1)} · {count}
      </span>
    </span>
  );
}

export default function CleaningCompaniesCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Session Z35 dialogs
  const [blastPreview, setBlastPreview] = useState(null); // {company, task_count, subject, html}
  const [blastBusy, setBlastBusy] = useState(false);
  const [rota, setRota] = useState(null); // {company, tasks}
  const [deliveries, setDeliveries] = useState(null); // {company, deliveries}
  const [heatmap, setHeatmap] = useState(null); // {company, days, cells}
  const [heatmapFilter, setHeatmapFilter] = useState(null); // {wd, h}
  const [retryingId, setRetryingId] = useState(null);
  const [backoffUnlockIso, setBackoffUnlockIso] = useState(null); // ISO of when retry re-opens
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!backoffUnlockIso) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [backoffUnlockIso]);
  const backoffRemaining = () => {
    if (!backoffUnlockIso) return 0;
    const diff = Math.max(0, new Date(backoffUnlockIso).getTime() - nowTick);
    return diff;
  };
  const backoffLabel = () => {
    const ms = backoffRemaining();
    if (ms <= 0) return "";
    const totalSec = Math.ceil(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cleaning-companies", { params: { include_inactive: true } });
      setRows(r.data || []);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      contact_name: row.contact_name || "",
      email: row.email || "",
      phone: row.phone || "",
      website: row.website || "",
      notes: row.notes || "",
      active: row.active !== false,
      whatsapp_enabled: !!row.whatsapp_enabled,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/cleaning-companies/${editing.id}`, form);
        toast.success(`${form.name} updated`);
      } else {
        await api.post("/cleaning-companies", form);
        toast.success(`${form.name} added`);
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const label = row.assigned_properties > 0
      ? `Deactivate "${row.name}"? It's still assigned to ${row.assigned_properties} property${row.assigned_properties === 1 ? "" : "ies"}.`
      : `Delete "${row.name}"?`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(label)) return;
    try {
      const r = await api.delete(`/cleaning-companies/${row.id}`);
      toast.success(r.data?.soft_deleted ? `${row.name} deactivated` : `${row.name} deleted`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || apiError(err));
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/cleaning-companies/${row.id}`, {
        ...row, active: !(row.active !== false),
      });
      toast.success(`${row.name} ${row.active !== false ? "deactivated" : "reactivated"}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || apiError(err));
    }
  };

  const previewBlast = async (row) => {
    setBlastBusy(true);
    try {
      const r = await api.post(`/cleaning-companies/${row.id}/email-blast`, { dry_run: true });
      setBlastPreview({ company: row, ...r.data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || apiError(err));
    } finally {
      setBlastBusy(false);
    }
  };

  const sendBlast = async () => {
    if (!blastPreview?.company) return;
    setBlastBusy(true);
    try {
      const r = await api.post(`/cleaning-companies/${blastPreview.company.id}/email-blast`, { dry_run: false });
      toast.success(`Sent to ${r.data.recipient} · ${r.data.task_count} turnovers`);
      setBlastPreview(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || apiError(err));
    } finally {
      setBlastBusy(false);
    }
  };

  const openRota = async (row) => {
    try {
      const r = await api.get(`/cleaning-companies/${row.id}/rota`, { params: { days: 30 } });
      setRota(r.data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openDeliveries = async (row) => {
    try {
      const [log, hm] = await Promise.all([
        api.get(`/cleaning-companies/${row.id}/deliveries`, { params: { limit: 50 } }),
        api.get(`/cleaning-companies/${row.id}/delivery-heatmap`, { params: { days: 28 } }),
      ]);
      setDeliveries(log.data);
      setHeatmap(hm.data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const retryDelivery = async (d) => {
    if (!deliveries?.company?.id) return;
    setRetryingId(d.id);
    try {
      const r = await api.post(
        `/cleaning-companies/${deliveries.company.id}/deliveries/${d.id}/retry`,
      );
      toast.success(`Retry ${r.data.channel === "whatsapp" ? "WhatsApp" : "email"} sent to ${r.data.recipient}`);
      // Refresh the log
      const refreshed = await api.get(
        `/cleaning-companies/${deliveries.company.id}/deliveries`,
        { params: { limit: 50 } },
      );
      setDeliveries(refreshed.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Session Z42 — 429 response includes retry_after_iso so we can show
      // the exact unlock time instead of a generic "try later" message.
      if (err?.response?.status === 429 && detail && typeof detail === "object" && detail.retry_after_iso) {
        setBackoffUnlockIso(detail.retry_after_iso);
        const unlockAt = new Date(detail.retry_after_iso);
        const label = unlockAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        toast.error(`Retry limit reached — next retry unlocks at ${label}`);
      } else {
        toast.error(
          (typeof detail === "string" ? detail : detail?.message) ||
          apiError(err, "Retry failed"),
        );
      }
    } finally {
      setRetryingId(null);
    }
  };

  // Group rota by date for calendar-like view
  const rotaByDate = rota
    ? rota.tasks.reduce((acc, t) => {
        (acc[t.date] = acc[t.date] || []).push(t);
        return acc;
      }, {})
    : {};

  return (
    <div className="ns-card p-8" data-testid="settings-cleaning-companies-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="ns-label text-xs text-slate-500 mb-1 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Cleaning companies
          </div>
          <h2 className="text-2xl font-normal">Who cleans which unit</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-lg">
            Manage suppliers, email them today&apos;s turnover list in one click, and star-rate each job so the best companies rise to the top.
          </p>
        </div>
        <Button
          onClick={startCreate}
          data-testid="settings-cleaning-company-add"
          className="rounded-full bg-teal-700 hover:bg-teal-800 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add company
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 italic">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          data-testid="settings-cleaning-companies-empty"
          className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500"
        >
          No cleaning companies yet — add your first supplier above.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div
              key={row.id}
              data-testid={`settings-cleaning-company-${row.id}`}
              className={`flex items-center gap-4 rounded-xl border p-4 transition ${
                row.active === false
                  ? "bg-slate-50/60 border-slate-200 opacity-70"
                  : "bg-white border-slate-100 hover:border-teal-200"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-slate-900 truncate">{row.name}</div>
                  {row.active === false && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">
                      Inactive
                    </span>
                  )}
                  {row.assigned_properties > 0 && (
                    <span
                      data-testid={`settings-cleaning-company-assignments-${row.id}`}
                      className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5"
                    >
                      {row.assigned_properties} propert{row.assigned_properties === 1 ? "y" : "ies"}
                    </span>
                  )}
                  <span data-testid={`settings-cleaning-company-rating-${row.id}`}>
                    <StarBar value={row.avg_rating} count={row.rating_count} />
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {row.contact_name && <span>{row.contact_name}</span>}
                  {row.email && <span>· {row.email}</span>}
                  {row.phone && <span>· {row.phone}</span>}
                  {row.website && <span>· {row.website}</span>}
                </div>
                {row.notes && (
                  <div className="text-[11px] text-slate-400 mt-1 italic truncate">{row.notes}</div>
                )}
                {row.last_blast_sent_at && (
                  <div className="text-[10px] text-emerald-700 mt-1">
                    Last blast: {new Date(row.last_blast_sent_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} · {row.last_blast_task_count} turnover{row.last_blast_task_count === 1 ? "" : "s"}
                  </div>
                )}
                {row.whatsapp_enabled && (
                  <div
                    data-testid={`settings-cleaning-company-whatsapp-flag-${row.id}`}
                    className="text-[10px] text-emerald-700 mt-1 inline-flex items-center gap-1"
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    WhatsApp rota on {row.phone ? `· ${row.phone}` : "(add a phone to send)"}
                    {row.last_whatsapp_sent_at && (
                      <span className="text-slate-400 ml-1">
                        · last sent {new Date(row.last_whatsapp_sent_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  data-testid={`settings-cleaning-company-history-${row.id}`}
                  onClick={() => openDeliveries(row)}
                  className="p-2 text-slate-400 hover:text-slate-800 rounded-lg hover:bg-stone-50"
                  title="Delivery history"
                >
                  <History className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  data-testid={`settings-cleaning-company-rota-${row.id}`}
                  onClick={() => openRota(row)}
                  className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg hover:bg-stone-50"
                  title="View rota"
                >
                  <CalendarClock className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  data-testid={`settings-cleaning-company-blast-${row.id}`}
                  onClick={() => previewBlast(row)}
                  disabled={!row.email || blastBusy || row.active === false}
                  className="p-2 text-slate-400 hover:text-teal-700 rounded-lg hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={row.email ? "Email today's turnovers" : "Add an email address first"}
                >
                  <Send className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  data-testid={`settings-cleaning-company-toggle-${row.id}`}
                  onClick={() => toggleActive(row)}
                  className="p-2 text-slate-400 hover:text-amber-600 rounded-lg hover:bg-stone-50"
                  title={row.active === false ? "Reactivate" : "Deactivate"}
                >
                  <Power className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  data-testid={`settings-cleaning-company-edit-${row.id}`}
                  onClick={() => startEdit(row)}
                  className="p-2 text-slate-400 hover:text-teal-700 rounded-lg hover:bg-stone-50"
                  title="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  data-testid={`settings-cleaning-company-delete-${row.id}`}
                  onClick={() => remove(row)}
                  className="p-2 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-stone-50"
                  title="Delete or deactivate"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="settings-cleaning-company-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit cleaning company" : "Add cleaning company"}</DialogTitle>
            <DialogDescription>
              Company-level supplier record — pick this company from a property to assign it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="ns-label">Company name</Label>
              <Input
                data-testid="settings-cleaning-company-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Sparkle Newport Ltd"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="ns-label">Contact person</Label>
                <Input
                  data-testid="settings-cleaning-company-form-contact"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="e.g. Anna Roberts"
                />
              </div>
              <div>
                <Label className="ns-label">Phone</Label>
                <Input
                  data-testid="settings-cleaning-company-form-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+44 7…"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="ns-label">Email</Label>
                <Input
                  data-testid="settings-cleaning-company-form-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="ops@sparkle.co.uk"
                />
              </div>
              <div>
                <Label className="ns-label">Website</Label>
                <Input
                  data-testid="settings-cleaning-company-form-website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="sparkle.co.uk"
                />
              </div>
            </div>
            <div>
              <Label className="ns-label">Notes</Label>
              <textarea
                data-testid="settings-cleaning-company-form-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder="Rate, day preferences, key handling instructions…"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                data-testid="settings-cleaning-company-form-active"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Active — show in property assignment dropdowns
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                data-testid="settings-cleaning-company-form-whatsapp"
                className="mt-0.5"
                checked={!!form.whatsapp_enabled}
                onChange={(e) => setForm({ ...form, whatsapp_enabled: e.target.checked })}
              />
              <span>
                Send daily rota to WhatsApp <span className="text-slate-400">(uses the phone number above via Twilio WhatsApp)</span>
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              data-testid="settings-cleaning-company-form-save"
              onClick={save}
              disabled={saving || !form.name.trim()}
              className="bg-teal-700 hover:bg-teal-800 text-white"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Add company"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Blast preview dialog */}
      <Dialog open={!!blastPreview} onOpenChange={(v) => !v && setBlastPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="settings-blast-preview-dialog">
          <DialogHeader>
            <DialogTitle>Today&apos;s turnovers · {blastPreview?.company?.name}</DialogTitle>
            <DialogDescription>
              {blastPreview?.task_count === 0
                ? "No turnovers today — you can still send a quiet-day note."
                : `${blastPreview?.task_count} turnover${blastPreview?.task_count === 1 ? "" : "s"} will be listed. Sends to ${blastPreview?.recipient}.`}
            </DialogDescription>
          </DialogHeader>
          <div
            data-testid="settings-blast-preview-html"
            className="rounded-xl border border-slate-100 bg-slate-50 p-4 max-h-[400px] overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: blastPreview?.html || "" }}
          />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setBlastPreview(null)} disabled={blastBusy}>
              Cancel
            </Button>
            <Button
              data-testid="settings-blast-send"
              onClick={sendBlast}
              disabled={blastBusy}
              className="bg-teal-700 hover:bg-teal-800 text-white"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {blastBusy ? "Sending…" : "Send now"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rota dialog */}
      <Dialog open={!!rota} onOpenChange={(v) => !v && setRota(null)}>
        <DialogContent className="max-w-2xl" data-testid="settings-rota-dialog">
          <DialogHeader>
            <DialogTitle>{rota?.company?.name} · Upcoming rota</DialogTitle>
            <DialogDescription>
              Next {rota?.days || 30} days — every property assigned to this company.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[440px] overflow-y-auto space-y-3 mt-2">
            {rota && (
              <div className="flex justify-end mb-2">
                <a
                  href={`/api/cleaning-companies/${rota.company.id}/rota.pdf?days=30`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="settings-rota-print"
                  className="text-[11px] rounded-full bg-slate-900 text-white px-3 py-1.5 hover:bg-slate-800 transition inline-flex items-center gap-1.5"
                >
                  📄 Print rota (landscape PDF)
                </a>
              </div>
            )}
            {rota && rota.tasks.length === 0 ? (
              <div className="text-sm text-slate-400 italic p-6 text-center rounded-xl border border-dashed border-slate-200">
                No upcoming turnovers assigned to this company.
              </div>
            ) : (
              Object.entries(rotaByDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, tasks]) => (
                <div key={date} data-testid={`settings-rota-day-${date}`}>
                  <div className="ns-label text-[10px] text-slate-500 mb-1.5">
                    {new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}
                  </div>
                  <div className="space-y-1">
                    {tasks.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between rounded-lg border border-slate-100 bg-white p-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{t.property_name || "Property"}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            Guest departing: {t.guest_name || "—"}
                          </div>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-3">
                          {t.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Session Z38 — Delivery history dialog */}
      <Dialog open={!!deliveries} onOpenChange={(v) => !v && (setDeliveries(null), setHeatmap(null))}>
        <DialogContent className="max-w-2xl" data-testid="settings-deliveries-dialog">
          <DialogHeader>
            <DialogTitle>{deliveries?.company?.name} · Delivery history</DialogTitle>
            <DialogDescription>
              Every rota email and WhatsApp burst — latest 50, newest first. Handy when a cleaner denies receipt.
            </DialogDescription>
          </DialogHeader>
          {heatmap && heatmap.cells && (
            <div className="rounded-xl border border-slate-100 bg-stone-50/60 p-3 mt-1" data-testid="settings-delivery-heatmap">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">
                  Last {heatmap.days} days · delivery heatmap
                </div>
                <div className="flex items-center gap-1 text-[10px] text-slate-400">
                  <span className="w-2.5 h-2.5 rounded-sm bg-slate-100 border border-slate-200" />
                  <span>none</span>
                  <span className="w-2.5 h-2.5 rounded-sm bg-emerald-200 ml-2" />
                  <span>ok</span>
                  <span className="w-2.5 h-2.5 rounded-sm bg-emerald-600 ml-1" />
                  <span className="w-2.5 h-2.5 rounded-sm bg-rose-500 ml-2" />
                  <span>fail</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <div className="inline-grid gap-[2px] text-[9px]" style={{ gridTemplateColumns: "28px repeat(24, 12px)" }}>
                  <div />
                  {Array.from({ length: 24 }).map((_, h) => (
                    <div key={h} className="text-center text-slate-400">{h % 3 === 0 ? h : ""}</div>
                  ))}
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, wd) => (
                    <>
                      <div key={`l-${wd}`} className="text-slate-500 pr-1 flex items-center justify-end">{d}</div>
                      {Array.from({ length: 24 }).map((_, h) => {
                        const cell = heatmap.cells.find((c) => c.weekday === wd && c.hour === h);
                        const sent = cell?.sent || 0;
                        const failed = cell?.failed || 0;
                        const delivered = cell?.delivered || 0;
                        let bg = "bg-slate-100 border border-slate-200";
                        if (sent > 0) {
                          if (failed === sent) bg = "bg-rose-500";
                          else if (failed > 0) bg = "bg-amber-400";
                          else if (delivered >= 3) bg = "bg-emerald-600";
                          else bg = "bg-emerald-300";
                        }
                        return (
                          <div
                            key={`${wd}-${h}`}
                            onClick={() => sent > 0 && setHeatmapFilter(
                              heatmapFilter && heatmapFilter.wd === wd && heatmapFilter.h === h
                                ? null
                                : { wd, h },
                            )}
                            title={sent === 0 ? "No sends" : `${delivered}/${sent} delivered${failed ? ` · ${failed} failed` : ""} — click to filter`}
                            className={`w-3 h-3 rounded-[2px] ${bg} ${sent > 0 ? "cursor-pointer hover:ring-2 hover:ring-teal-400" : ""} ${heatmapFilter && heatmapFilter.wd === wd && heatmapFilter.h === h ? "ring-2 ring-teal-600" : ""}`}
                            data-testid={`settings-delivery-heatmap-cell-${wd}-${h}`}
                          />
                        );
                      })}
                    </>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end mb-2">
            {deliveries?.deliveries?.length > 0 && (
              <a
                href={`/api/cleaning-companies/${deliveries.company.id}/deliveries.csv?limit=500`}
                target="_blank"
                rel="noreferrer"
                data-testid="settings-deliveries-csv"
                className="text-[11px] rounded-full bg-slate-900 text-white px-3 py-1.5 hover:bg-slate-800 transition inline-flex items-center gap-1.5"
              >
                ⬇ Download CSV
              </a>
            )}
          </div>
          <div className="max-h-[440px] overflow-y-auto space-y-2 mt-2">
            {deliveries && deliveries.deliveries.length === 0 ? (
              <div
                data-testid="settings-deliveries-empty"
                className="text-sm text-slate-400 italic p-6 text-center rounded-xl border border-dashed border-slate-200"
              >
                Nothing sent yet — trigger a manual blast or wait for the 07:30 UTC cron.
              </div>
            ) : (
              (deliveries?.deliveries || [])
                .filter((d) => {
                  if (!heatmapFilter) return true;
                  const ts = new Date(d.sent_at);
                  // weekday: JS 0=Sun..6=Sat, we store 0=Mon..6=Sun
                  const wd = (ts.getUTCDay() + 6) % 7;
                  const h = ts.getUTCHours();
                  return wd === heatmapFilter.wd && h === heatmapFilter.h;
                })
                .map((d) => (
                <div
                  key={d.id}
                  data-testid={`settings-delivery-${d.id}`}
                  className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
                    d.ok
                      ? "border-emerald-100 bg-emerald-50/40"
                      : "border-rose-100 bg-rose-50/40"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${
                        d.channel === "whatsapp"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-indigo-50 text-indigo-800"
                      }`}>
                        {d.channel === "whatsapp" ? "WhatsApp" : "Email"}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[11px] ${
                        d.ok ? "text-emerald-700" : "text-rose-700"
                      }`}>
                        {d.ok ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                        {d.ok ? "Delivered" : "Failed"}
                      </span>
                      <span className="text-[11px] text-slate-500 truncate">
                        · {d.task_count} turnover{d.task_count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 truncate">
                      to {d.recipient || "—"}
                      {d.reference && (
                        <span className="text-slate-400 ml-2 font-mono">{d.reference.slice(0, 20)}</span>
                      )}
                    </div>
                    {!d.ok && d.error && (
                      <div className="text-[11px] text-rose-600 mt-0.5 truncate italic">
                        {d.error}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 whitespace-nowrap">
                    <div className="text-[11px] text-slate-400">
                      {new Date(d.sent_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    {!d.ok && (
                      backoffRemaining() > 0 ? (
                        <span
                          data-testid={`settings-delivery-retry-backoff-${d.id}`}
                          className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2.5 py-1 inline-flex items-center gap-1"
                          title="Retry limit hit — waiting for the sliding window to open"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                          Locked · {backoffLabel()}
                        </span>
                      ) : (
                        <button
                          type="button"
                          data-testid={`settings-delivery-retry-${d.id}`}
                          onClick={() => retryDelivery(d)}
                          disabled={retryingId === d.id}
                          className="text-[10px] rounded-full bg-slate-900 text-white px-2.5 py-1 hover:bg-slate-800 transition disabled:opacity-50"
                          title="Re-fire the same rota through this channel"
                        >
                          {retryingId === d.id ? "Retrying…" : "Retry now"}
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
